import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Clock, Save, Plus, Trash2 } from "lucide-react";
import api from "@/lib/api";

const JOURS = [
  [1, "Lundi"], [2, "Mardi"], [3, "Mercredi"], [4, "Jeudi"],
  [5, "Vendredi"], [6, "Samedi"], [7, "Dimanche"],
];
const CHAMPS = [
  ["morning_start", "Matin — entrée"],
  ["morning_end", "Matin — sortie"],
  ["afternoon_start", "Après-midi — entrée"],
  ["afternoon_end", "Après-midi — sortie"],
];

export default function PointageHoraireCard() {
  const [versions, setVersions] = useState([]);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const fetch = useCallback(async (annule = () => false) => {
    setLoading(true);
    try {
      const { data } = await api.get("/pointage/horaires");
      if (annule()) return;
      setVersions(data.versions || []);
      const derniere = (data.versions || [])[data.versions.length - 1];
      setForm(derniere ? { ...derniere, jours: derniere.jours || {} } : null);
    } finally { if (!annule()) setLoading(false); }
  }, []);

  useEffect(() => {
    let annule = false;
    fetch(() => annule);
    return () => { annule = true; };
  }, [fetch]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setJour = (j, champ, valeur) => setForm((f) => {
    const jours = { ...(f.jours || {}) };
    const cur = { ...(jours[String(j)] || {}) };
    if (!valeur) delete cur[champ]; else cur[champ] = valeur;
    if (Object.keys(cur).length) jours[String(j)] = cur; else delete jours[String(j)];
    return { ...f, jours };
  });
  const toggleJourTravaille = (j) => setForm((f) => {
    const wd = f.work_days || [];
    return { ...f, work_days: wd.includes(j) ? wd.filter((x) => x !== j) : [...wd, j].sort() };
  });

  const enregistrer = async () => {
    setBusy(true);
    try {
      await api.post("/pointage/horaires", {
        date_debut: form.date_debut,
        morning_start: form.morning_start, morning_end: form.morning_end,
        afternoon_start: form.afternoon_start, afternoon_end: form.afternoon_end,
        target_hours_day: Number(form.target_hours_day) || 8,
        work_days: form.work_days || [1, 2, 3, 4, 5],
        jours: form.jours || {},
        commentaire: form.commentaire || "",
      });
      toast.success(`Horaire enregistré, applicable dès le ${form.date_debut}`);
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const supprimer = async (v) => {
    try {
      await api.delete(`/pointage/horaires/${v.id}`);
      toast.success("Horaire supprimé");
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const nouvelleVersion = () => {
    const d = new Date();
    const dd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
    setForm((f) => ({ ...f, id: undefined, date_debut: dd, commentaire: "Nouvel horaire" }));
    toast.info("Choisissez la date de prise d'effet, puis enregistrez");
  };

  if (loading) return <Card className="p-6 border-gray-200"><Loader2 className="w-5 h-5 animate-spin mx-auto text-gray-400" /></Card>;
  if (!form) return null;

  return (
    <Card className="p-4 border-gray-200" data-testid="pointage-horaire-card">
      <h3 className="font-heading font-semibold text-gray-900 flex items-center gap-2">
        <Clock className="w-5 h-5 text-[#52a675]" /> Horaire du garage
      </h3>
      <p className="text-xs text-gray-500 mt-1 mb-4">
        Horaire de référence pour tous les collaborateurs. Les retards d'un jour donné sont calculés
        avec l'horaire en vigueur ce jour-là : changer l'horaire ne modifie jamais les mois passés.
      </p>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <Label className="text-xs">Applicable à partir du</Label>
          <Input type="date" value={form.date_debut || ""} onChange={(e) => set("date_debut", e.target.value)}
                 className="mt-1 w-44" data-testid="horaire-date-debut" />
        </div>
        <div className="flex-1 min-w-[180px]">
          <Label className="text-xs">Commentaire</Label>
          <Input value={form.commentaire || ""} onChange={(e) => set("commentaire", e.target.value)}
                 className="mt-1" data-testid="horaire-commentaire" />
        </div>
        <Button variant="outline" onClick={nouvelleVersion} data-testid="horaire-nouvelle-version">
          <Plus className="w-4 h-4 mr-2" /> Nouvel horaire à une date
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {CHAMPS.map(([k, l]) => (
          <div key={k}>
            <Label className="text-xs text-gray-600">{l}</Label>
            <Input type="time" value={form[k] || ""} onChange={(e) => set(k, e.target.value)}
                   className="mt-1" data-testid={`horaire-${k}`} />
          </div>
        ))}
        <div>
          <Label className="text-xs text-gray-600">Heures à faire / jour</Label>
          <Input type="number" step="0.25" value={form.target_hours_day ?? 8}
                 onChange={(e) => set("target_hours_day", e.target.value)}
                 className="mt-1" data-testid="horaire-target-hours" />
        </div>
      </div>

      <div className="mt-4">
        <Label className="text-xs text-gray-600">Jours travaillés</Label>
        <div className="flex flex-wrap gap-1.5 mt-1">
          {JOURS.map(([j, l]) => (
            <button key={j} type="button" onClick={() => toggleJourTravaille(j)}
                    className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${
                      (form.work_days || []).includes(j)
                        ? "bg-[#52a675] text-white border-[#52a675]"
                        : "bg-white text-gray-600 border-gray-200 hover:border-[#52a675]"}`}
                    data-testid={`horaire-jour-${j}`}>
              {l.slice(0, 3)}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4">
        <Label className="text-xs text-gray-600">Horaire différent selon le jour</Label>
        <p className="text-[11px] text-gray-400 mb-1">
          Laissez vide pour utiliser l'horaire général ci-dessus (ex. vendredi qui finit plus tôt).
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-left text-[10px] uppercase text-gray-500">
              <tr>
                <th className="py-1">Jour</th>
                {CHAMPS.map(([k, l]) => <th key={k} className="py-1">{l}</th>)}
                <th className="py-1">Heures/jour</th>
              </tr>
            </thead>
            <tbody data-testid="horaire-jours-table">
              {JOURS.filter(([j]) => (form.work_days || []).includes(j)).map(([j, l]) => (
                <tr key={j} className="border-t border-gray-100">
                  <td className="py-1 pr-2 text-gray-700">{l}</td>
                  {CHAMPS.map(([k]) => (
                    <td key={k} className="py-1 pr-2">
                      <Input type="time" value={form.jours?.[String(j)]?.[k] || ""}
                             onChange={(e) => setJour(j, k, e.target.value)}
                             className="h-8 w-28" data-testid={`horaire-jour-${j}-${k}`} />
                    </td>
                  ))}
                  <td className="py-1">
                    <Input type="number" step="0.25" value={form.jours?.[String(j)]?.target_hours_day || ""}
                           onChange={(e) => setJour(j, "target_hours_day", e.target.value ? Number(e.target.value) : "")}
                           className="h-8 w-20" placeholder="—" data-testid={`horaire-jour-${j}-heures`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Button onClick={enregistrer} disabled={busy} className="mt-4 bg-[#52a675] hover:bg-[#3d8a5c]"
              data-testid="horaire-save">
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
        Enregistrer l'horaire
      </Button>

      {versions.length > 1 && (
        <div className="mt-5 border-t border-gray-100 pt-3">
          <Label className="text-xs text-gray-600">Historique des horaires</Label>
          <ul className="mt-1 space-y-1" data-testid="horaire-versions">
            {versions.map((v) => (
              <li key={v.id} className="flex items-center gap-2 text-xs text-gray-600">
                <span className="font-mono">dès le {v.date_debut}</span>
                <span>{v.morning_start}–{v.morning_end} · {v.afternoon_start}–{v.afternoon_end}</span>
                <span className="text-gray-400">{v.commentaire}</span>
                <Button size="sm" variant="ghost" className="ml-auto h-7" onClick={() => supprimer(v)}
                        data-testid={`horaire-delete-${v.id}`}>
                  <Trash2 className="w-3.5 h-3.5 text-red-500" />
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}
